import type { Node as PMNode } from '@tiptap/pm/model'
import type { EditorState } from '@tiptap/pm/state'
import {
  absolutePositionToRelativePosition,
  type ProsemirrorBinding,
  relativePositionToAbsolutePosition,
  ySyncPluginKey,
} from 'y-prosemirror'
import {
  createAbsolutePositionFromRelativePosition,
  createRelativePositionFromTypeIndex,
  type RelativePosition,
  type Doc as YDoc,
} from 'yjs'
import type { ProposalRender } from '#src/components/rich-text-editor/AIProposalsExtension.ts'

// The ONLY module that couples to yjs / y-prosemirror. Everything Strategy A
// needs — capturing Yjs relative positions at stage time and re-deriving PM
// positions post-settle — lives here so the extension and the reconciler stay
// otherwise yjs-agnostic, and so 2c-4's real two-client harness can drive the
// same functions (and unit tests can `vi.mock` this one module).

// A pair of Yjs relative positions anchoring a proposal's [from,to]. Unlike the
// plain PM offsets on AIProposal, these survive a whole-doc ReplaceStep: they
// ride the surviving neighbor content and re-resolve to the correct PM position
// after y-prosemirror reconciles its binding. In-memory only (never persisted).
export interface RelAnchor {
  from: RelativePosition
  to: RelativePosition
  // Capture-time point-vs-span topology — the signal the post-settle collapse
  // guard keys on. `kind` cannot carry it: a 'replace' is legitimately staged
  // as a point (replace-nothing-with-text from the diff pipeline) OR a span,
  // and validity means the topology SURVIVED — a span that resolves collapsed
  // has lost its target (accepting would insert without replacing), a point
  // whose ends diverged has lost its meaning (accepting would delete
  // collaborator content). Matches the extension's incremental-mapping guard,
  // which keys on the same pre-transaction collapsedness.
  wasCollapsed: boolean
}

// A STRUCTURAL fingerprint of the anchor target — container/type survived, NOT
// exact text (M2 finding 3). An exact-text check would re-import Strategy B's
// false-drop when a remote co-editor edits the very words the proposal anchors.
// Lenient for text (the container textblock must survive); strict for the choice
// atom (a resolved position that is not the SAME field is a real conflict).
export interface StructuralFingerprint {
  // Node-type name of the textblock/container resolved at `from` when captured.
  fromParentType: string
  // Present only when the anchor targets a choice atom: the field id that must
  // still be the node under `from` for the reposition to be trusted.
  choiceFieldId?: string
}

// Read y-prosemirror's live binding off the editor state. Absent for a
// non-collab editor (no ySyncPlugin) and in unit tests — callers treat that as
// "Strategy A unavailable" and fall back to content re-resolution.
function getBinding(state: EditorState): ProsemirrorBinding | undefined {
  const sync = ySyncPluginKey.getState(state) as
    | { binding?: ProsemirrorBinding }
    | undefined
  return sync?.binding
}

// Re-associate a captured position to the LEFT (assoc -1) — it then rides the
// item BEFORE the index, so an insertion at exactly that index lands to its
// right instead of being absorbed. y-prosemirror's helper owns the tree walk
// that finds the inner Y.XmlText and offset and hardcodes assoc 0; round-tripping
// through the absolute (type, index) it landed on keeps that walk and only
// changes the bias.
function withLeftAssoc(rel: RelativePosition, doc: YDoc): RelativePosition {
  const abs = createAbsolutePositionFromRelativePosition(rel, doc)
  return abs
    ? createRelativePositionFromTypeIndex(abs.type, abs.index, -1)
    : rel
}

// Capture relative anchors for [from,to] against the current Yjs binding.
// Returns null when there is no collab binding.
//
// Association bias (design §3.4 invariant) must match how the plugin's ordinary
// local-edit path maps the same anchors — `mapResult(from, 1)` / `mapResult(to,
// -1)`, an inward-biased range — and how the insert widget paints (`side: 1`).
// y-prosemirror right-associates everything, so only `to` needs flipping, and
// only for a real range: a collapsed insert caret keeps both ends on the same
// bias, since a from/to that diverge under a concurrent insert would resolve
// non-collapsed and drop the proposal.
export function captureRelAnchor(
  state: EditorState,
  from: number,
  to: number
): RelAnchor | null {
  const binding = getBinding(state)
  if (!binding) {
    return null
  }
  const toRel = absolutePositionToRelativePosition(
    to,
    binding.type,
    binding.mapping
  )
  const wasCollapsed = from === to
  return {
    from: absolutePositionToRelativePosition(
      from,
      binding.type,
      binding.mapping
    ),
    to: wasCollapsed ? toRel : withLeftAssoc(toRel, binding.doc),
    wasCollapsed,
  }
}

// Capture the structural fingerprint of the anchor target in the current doc.
// Keyed on `from`: the container/type at the anchor start is what the drift
// check re-verifies, so the `to` end is not part of the fingerprint.
export function captureFingerprint(
  doc: PMNode,
  from: number,
  render: ProposalRender
): StructuralFingerprint {
  const clampedFrom = Math.min(Math.max(from, 0), doc.content.size)
  const fromParentType = doc.resolve(clampedFrom).parent.type.name
  if (render.type === 'choiceOption') {
    const node = doc.nodeAt(clampedFrom)
    const fieldId = node?.attrs.fieldId
    return {
      fromParentType,
      choiceFieldId: typeof fieldId === 'string' ? fieldId : undefined,
    }
  }
  return { fromParentType }
}

// Re-derive the PM [from,to] for a stale proposal from its relative anchors,
// gated by the structural drift check. Returns null — meaning DROP — only when
// the anchored content is genuinely unrecoverable: its Yjs container was removed
// from the tree, the resolved range is inverted/degenerate, or the structural
// fingerprint no longer holds. A remote co-edit of the anchored words, an
// undo/redo, or a setContent diff all re-resolve cleanly (M2 6/6 + 4b/4c).
export function resolveRelAnchor(
  state: EditorState,
  anchor: RelAnchor,
  fingerprint: StructuralFingerprint | undefined
): { from: number; to: number } | null {
  const binding = getBinding(state)
  if (!binding) {
    return null
  }
  const from = relativePositionToAbsolutePosition(
    binding.doc,
    binding.type,
    anchor.from,
    binding.mapping
  )
  const to = relativePositionToAbsolutePosition(
    binding.doc,
    binding.type,
    anchor.to,
    binding.mapping
  )
  if (from === null || to === null) {
    return null
  }
  const doc = state.doc
  // y-prosemirror returns `pos - 1`, so an anchor whose every ancestor was
  // remotely deleted resolves to -1 rather than null; doc.resolve would throw.
  if (from < 0 || to < 0 || from > doc.content.size || to > doc.content.size) {
    return null
  }
  // The capture-time topology must SURVIVE: a point (insert caret or
  // insertion-shaped replace) must still be a point — diverged ends mean
  // accepting would delete collaborator content between them — and a span must
  // still be a non-empty, non-inverted span — a collapsed one means its target
  // was deleted, and accepting would insert replacement text with nothing being
  // replaced. Same rule as the extension's incremental-mapping guard.
  if (anchor.wasCollapsed ? from !== to : from >= to) {
    return null
  }
  if (fingerprint && !fingerprintSurvives(doc, from, fingerprint)) {
    return null
  }
  return { from, to }
}

// Structural drift check: has the anchor's container/type survived? Strict for
// the choice atom (same fieldId), lenient for text (parent is still a
// textblock) so co-editing the anchored words does not force a drop.
function fingerprintSurvives(
  doc: PMNode,
  from: number,
  fingerprint: StructuralFingerprint
): boolean {
  if (fingerprint.choiceFieldId !== undefined) {
    const node = doc.nodeAt(from)
    return (
      node?.type.name === 'choice' &&
      node.attrs.fieldId === fingerprint.choiceFieldId
    )
  }
  return doc.resolve(from).parent.isTextblock
}
